import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import './UserBadge.css';

// Badge configuration with metadata
const BADGE_CONFIG = {
  // Early Adopter Badges
  founding_member: {
    name: "badges.badge_names.founding_member",
    description: "badges.badge_descriptions.founding_member",
    icon: "🎖️",
    rarity: "diamond",
    category: "early_adopter"
  },
  early_adopter: {
    name: "badges.badge_names.early_adopter", 
    description: "badges.badge_descriptions.early_adopter",
    icon: "🚀",
    rarity: "gold",
    category: "early_adopter"
  },
  first_post_pioneer: {
    name: "badges.badge_names.first_post_pioneer",
    description: "badges.badge_descriptions.first_post_pioneer",
    icon: "📝",
    rarity: "gold", 
    category: "early_adopter"
  },
  package_trailblazer: {
    name: "badges.badge_names.package_trailblazer",
    description: "badges.badge_descriptions.package_trailblazer",
    icon: "📦",
    rarity: "gold",
    category: "early_adopter"
  },

  // Activity Badges
  super_active: {
    name: "badges.badge_names.super_active",
    description: "badges.badge_descriptions.super_active",
    icon: "🔥",
    rarity: "silver",
    category: "activity"
  },
  century_club: {
    name: "badges.badge_names.century_club",
    description: "badges.badge_descriptions.century_club",
    icon: "💯", 
    rarity: "gold",
    category: "activity"
  },
  consistent_rider: {
    name: "badges.badge_names.consistent_rider",
    description: "badges.badge_descriptions.consistent_rider",
    icon: "🎯",
    rarity: "silver",
    category: "activity"
  },
  explorer: {
    name: "badges.badge_names.explorer",
    description: "badges.badge_descriptions.explorer", 
    icon: "🌍",
    rarity: "silver",
    category: "activity"
  },

  // Quality Badges
  five_star_driver: {
    name: "badges.badge_names.five_star_driver",
    description: "badges.badge_descriptions.five_star_driver",
    icon: "⭐",
    rarity: "gold",
    category: "quality"
  },
  reliable_partner: {
    name: "badges.badge_names.reliable_partner", 
    description: "badges.badge_descriptions.reliable_partner",
    icon: "🤝",
    rarity: "silver",
    category: "quality"
  },
  trusted_member: {
    name: "badges.badge_names.trusted_member",
    description: "badges.badge_descriptions.trusted_member",
    icon: "🛡️", 
    rarity: "silver",
    category: "quality"
  },
  quick_responder: {
    name: "badges.badge_names.quick_responder",
    description: "badges.badge_descriptions.quick_responder",
    icon: "⚡",
    rarity: "bronze",
    category: "quality"
  },

  // Package Specialist Badges
  package_pro: {
    name: "badges.badge_names.package_pro",
    description: "badges.badge_descriptions.package_pro",
    icon: "📮",
    rarity: "silver",
    category: "package"
  },
  delivery_champion: {
    name: "badges.badge_names.delivery_champion",
    description: "badges.badge_descriptions.delivery_champion", 
    icon: "🏆",
    rarity: "gold",
    category: "package"
  },
  express_delivery: {
    name: "badges.badge_names.express_delivery",
    description: "badges.badge_descriptions.express_delivery",
    icon: "🚚",
    rarity: "silver", 
    category: "package"
  },

  // Special Achievement Badges
  anniversary: {
    name: "badges.badge_names.anniversary",
    description: "badges.badge_descriptions.anniversary",
    icon: "🎂",
    rarity: "gold",
    category: "special"
  },
  marathon_driver: {
    name: "badges.badge_names.marathon_driver", 
    description: "badges.badge_descriptions.marathon_driver",
    icon: "🏃‍♂️",
    rarity: "gold",
    category: "special"
  },
  perfect_month: {
    name: "badges.badge_names.perfect_month",
    description: "badges.badge_descriptions.perfect_month",
    icon: "💎",
    rarity: "diamond",
    category: "special" 
  },
  route_master: {
    name: "badges.badge_names.route_master",
    description: "badges.badge_descriptions.route_master",
    icon: "🗺️",
    rarity: "silver",
    category: "special"
  },
  pioneer: {
    name: "badges.badge_names.pioneer",
    description: "badges.badge_descriptions.pioneer",
    icon: "🚀",
    rarity: "gold",
    category: "early_adopter"
  },
  community_champion: {
    name: "badges.badge_names.community_champion",
    description: "badges.badge_descriptions.community_champion",
    icon: "🏆",
    rarity: "gold",
    category: "special"
  },
  super_connector: {
    name: "badges.badge_names.super_connector",
    description: "badges.badge_descriptions.super_connector",
    icon: "🌐",
    rarity: "silver",
    category: "activity"
  }
};

/**
 * UserBadge Component - Displays user achievement badges
 * Supports 3 sizes: small (16px), medium (24px), large (32px)
 */
const UserBadge = ({ 
  badgeType, 
  size = 'medium', 
  showTooltip = true,
  className = '',
  style = {},
  onClick = null,
  isNew = false,
  isLoading = false
}) => {
  const { t } = useTranslation('common');
  const [showTooltipState, setShowTooltipState] = useState(false);
  
  // Get badge configuration
  const badge = BADGE_CONFIG[badgeType];
  
  if (!badge) {
    console.warn(`Unknown badge type: ${badgeType}`);
    return null;
  }

  // Get translated name and description
  const badgeName = t(badge.name, badgeType);
  const badgeDescription = t(badge.description, badge.description);

  // Size configurations
  const sizeConfig = {
    small: {
      size: '16px',
      fontSize: '10px',
      borderWidth: '1px'
    },
    medium: {
      size: '24px', 
      fontSize: '14px',
      borderWidth: '2px'
    },
    large: {
      size: '32px',
      fontSize: '18px',
      borderWidth: '2px'
    }
  };

  const config = sizeConfig[size] || sizeConfig.medium;

  // Rarity colors using your CSS variables
  const rarityColors = {
    bronze: '#CD7F32',
    silver: '#C0C0C0', 
    gold: 'var(--accent-color)',
    diamond: 'var(--accent-color2)'
  };

  const badgeStyles = {
    width: config.size,
    height: config.size,
    borderRadius: '50%',
    border: `${config.borderWidth} solid ${rarityColors[badge.rarity]}`,
    backgroundColor: 'var(--background-color)',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: config.fontSize,
    cursor: onClick ? 'pointer' : showTooltip ? 'help' : 'default',
    position: 'relative',
    boxShadow: badge.rarity === 'diamond' ? '0 0 8px rgba(90, 157, 201, 0.4)' : 
               badge.rarity === 'gold' ? '0 0 6px rgba(159, 193, 49, 0.3)' : 
               '0 1px 3px rgba(0, 0, 0, 0.2)',
    transition: 'all 0.2s ease',
    ...style
  };

  const tooltipStyles = {
    position: 'absolute',
    top: size === 'small' ? '20px' : size === 'medium' ? '30px' : '40px',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: 'var(--main-color)',
    color: 'var(--BackG-color)',
    padding: '8px 12px',
    borderRadius: '8px',
    fontSize: '12px',
    whiteSpace: 'nowrap',
    zIndex: 1000,
    boxShadow: 'var(--Box-shadow)',
    opacity: showTooltipState ? 1 : 0,
    visibility: showTooltipState ? 'visible' : 'hidden',
    transition: 'all 0.2s ease',
    pointerEvents: 'none',
    maxWidth: '200px',
    whiteSpace: 'normal',
    textAlign: 'center',
    lineHeight: '1.3'
  };

  const handleMouseEnter = () => {
    if (showTooltip && !isLoading) {
      setShowTooltipState(true);
    }
  };

  const handleMouseLeave = () => {
    setShowTooltipState(false);
  };

  const handleClick = (e) => {
    if (onClick && !isLoading) {
      e.preventDefault();
      e.stopPropagation();
      onClick(badgeType, badge);
    }
  };

  // Build CSS classes
  const cssClasses = [
    'user-badge',
    `user-badge-${badge.rarity}`,
    `user-badge-${size}`,
    isNew && 'user-badge-new',
    isLoading && 'user-badge-loading',
    className
  ].filter(Boolean).join(' ');

  return (
    <div 
      className={cssClasses}
      style={badgeStyles}
      data-category={badge.category}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
      title={!showTooltip ? `${badgeName}: ${badgeDescription}` : ''}
    >
      <span className="badge-icon">{badge.icon}</span>
      
      {showTooltip && (
        <div className="badge-tooltip" style={tooltipStyles}>
          <div className="badge-tooltip-title" style={{ fontWeight: 'bold', marginBottom: '2px' }}>
            {badgeName}
          </div>
          <div className="badge-tooltip-description" style={{ fontSize: '11px', opacity: 0.9 }}>
            {badgeDescription}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * UserBadgeGroup Component - Displays multiple badges in a row
 */
const UserBadgeGroup = ({ 
  badges = [], 
  size = 'medium', 
  maxVisible = 3,
  showTooltip = true,
  spacing = '4px',
  onClick = null,
  className = '',
  style = {}
}) => {
  if (!badges || badges.length === 0) return null;

  const visibleBadges = badges.slice(0, maxVisible);
  const remainingCount = badges.length - maxVisible;

  const groupStyles = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: spacing,
    flexWrap: 'nowrap',
    ...style
  };

  const moreIndicatorStyles = {
    fontSize: size === 'small' ? '10px' : size === 'medium' ? '12px' : '14px',
    color: 'var(--secondary-color)',
    fontWeight: 'bold',
    marginLeft: '2px'
  };

  return (
    <div className={`user-badge-group ${className}`} style={groupStyles}>
      {visibleBadges.map((badgeType, index) => (
        <UserBadge
          key={`${badgeType}-${index}`}
          badgeType={badgeType}
          size={size}
          showTooltip={showTooltip}
          onClick={onClick}
        />
      ))}
      
      {remainingCount > 0 && (
        <span 
          className="badge-more-indicator"
          style={moreIndicatorStyles}
          title={`+${remainingCount} more badge${remainingCount > 1 ? 's' : ''}`}
        >
          +{remainingCount}
        </span>
      )}
    </div>
  );
};

UserBadge.propTypes = {
  badgeType: PropTypes.string.isRequired,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  showTooltip: PropTypes.bool,
  className: PropTypes.string,
  style: PropTypes.object,
  onClick: PropTypes.func,
  isNew: PropTypes.bool,
  isLoading: PropTypes.bool
};

UserBadgeGroup.propTypes = {
  badges: PropTypes.arrayOf(PropTypes.string),
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  maxVisible: PropTypes.number,
  showTooltip: PropTypes.bool,
  spacing: PropTypes.string,
  onClick: PropTypes.func,
  className: PropTypes.string,
  style: PropTypes.object
};

// Export badge configuration for use in other components
export { BADGE_CONFIG };

// Export both components
export { UserBadge, UserBadgeGroup };
export default UserBadge;
